import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import {
  MenuItem,
  SalvButton,
  SalvIconButton,
  SalvMenuButton,
  SalvSwitch,
  SalvTextArea,
  SalvTooltipWrapper,
  Size,
} from '@salvapp/ui-components';
import { faBell, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { clsx } from 'clsx';
import { SelectorOptionMap, SelectorOptionType } from './Selector';
import { InternalApiListSelectorGroup } from '../../../api-internal';
import ExpanderButton from '../../../common/ExpanderButton';
import { Virtualizer } from '@tanstack/react-virtual';
import Has from '../../../auth/Has';
import { CAN_MODIFY_SCREENING_SELECTOR_GROUP } from '../../../auth/permissions';

interface Props {
  index: number;
  option: SelectorOptionType;
  setOptions: Dispatch<SetStateAction<SelectorOptionMap>>;
  navigate?: () => void;
  navigated?: boolean;
  level: number;
  optionDepth: number;
  isEdit: boolean;
  measureElement: Virtualizer<HTMLDivElement, Element>['measureElement'];
}

const isLeaf = (option: SelectorOptionType): boolean => {
  return option.children.length === 0;
};

const isSubscribable = (option: SelectorOptionType, level: number, optionDepth: number) =>
  level === optionDepth - 1 && option.children.some((child) => isLeaf(child));

const areAllLeavesSelected = (option: SelectorOptionType): boolean => {
  if (isLeaf(option)) {
    return !!option.isSelected;
  }
  return option.children.every((child) => areAllLeavesSelected(child));
};

export const isAnyLeafSelected = (option: SelectorOptionType): boolean => {
  if (isLeaf(option)) {
    return !!option.isSelected;
  }
  return option.children.some((child) => isAnyLeafSelected(child));
};

export const isAnyChildSubscribed = (option: SelectorOptionType, level: number, optionDepth: number): boolean => {
  if (isSubscribable(option, level, optionDepth)) {
    return !!option.isSubscribed;
  }
  return option.children.some((child) => isAnyChildSubscribed(child, level + 1, optionDepth));
};

const updateAllLeavesAndSubscribe = (
  option: SelectorOptionType,
  selected: boolean,
  level: number,
  optionDepth: number
) => {
  if (isLeaf(option)) {
    option.isSelected = selected;
  } else {
    if (isSubscribable(option, level, optionDepth)) {
      option.isSubscribed = selected;
    }

    option.children.forEach((child) => {
      updateAllLeavesAndSubscribe(child, selected, level + 1, optionDepth);
    });
  }
};

const SelectorOption = ({
  index,
  option,
  setOptions,
  navigate,
  navigated,
  level,
  optionDepth,
  isEdit,
  measureElement,
}: Props) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      onClick={() => navigate?.()}
      className={clsx('selector-option', {
        'selector-option-navigable': !!navigate,
        'selector-option-navigated': !!navigated,
      })}
      ref={measureElement} // get the correct height of the selector option
      data-index={index}
    >
      <div className={'selector-option-main'}>
        <div className={'selector-option-title'}>{option.title}</div>
        <div
          className={'selector-option-actions'}
          // onClick={(event) => {
          //   // don't trigger parent on click when actions are clicked
          //   event.stopPropagation();
          // }}
        >
          {isSubscribable(option, level, optionDepth) ? (
            <SalvTooltipWrapper
              tooltip={
                option.isSubscribed
                  ? 'Unsubscribing stops automatically enabling new programs added in the future'
                  : 'Subscribing automatically enables new programs added in the future'
              }
            >
              {/* would be more correct to use something more close to a checkbox or switch here*/}
              <SalvButton
                ariaLabel={`Subscribe to ${option.title}`}
                icon={faBell}
                variant={option.isSubscribed ? 'primary' : 'muted'}
                onClick={() => {
                  option.isSubscribed = !option.isSubscribed;
                  setOptions((prev) => new Map(prev));
                }}
                size={Size.EXTRA_SMALL}
                disabled={!isEdit}
              />
            </SalvTooltipWrapper>
          ) : (
            isAnyChildSubscribed(option, level, optionDepth) && (
              <SalvTooltipWrapper tooltip={'Subscription is enabled under this level'}>
                <div className={'subscription-notification'} />
              </SalvTooltipWrapper>
            )
          )}
          <SalvSwitch
            selected={isLeaf(option) ? option.isSelected : isAnyLeafSelected(option)}
            onChange={(selected) => {
              if (isLeaf(option)) {
                option.isSelected = selected;
              } else {
                updateAllLeavesAndSubscribe(option, selected, level, optionDepth);
              }

              setOptions((prev) => new Map(prev));
            }}
            indeterminate={!isLeaf(option) && !areAllLeavesSelected(option)}
            ariaLabel={`Enable ${option.title}`}
            disabled={!isEdit}
            // when a leaf node is not on the max level
            className={isLeaf(option) && optionDepth > level ? 'aligned-switch' : ''}
          />
          {!isLeaf(option) && (
            <SalvIconButton
              ariaLabel={`Navigate from ${option.title}`}
              icon={faChevronRight}
              onClick={() => navigate?.()}
            />
          )}
        </div>
      </div>
      {/*descriptions on non-leaf nodes look ugly, will hide for demo*/}
      {optionDepth === level && option.descriptions.length > 0 && (
        <>
          {expanded && (
            <div className={'m-t-8'} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {option.descriptions.map((description, i) => (
                <span key={i}>{description}</span>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'end' }}>
            <ExpanderButton
              isExpanded={expanded}
              ariaLabel={`More info for ${option.title}`}
              onClick={() => {
                setExpanded((prevState) => !prevState);
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export type GroupValidationResult = { nameIsBlank: boolean; noOptionsAreSelected: boolean };

interface SelectorGroupProps {
  name: string;
  setName: (value: string) => void;
  navigate: () => void;
  isNavigated?: boolean;
  group: InternalApiListSelectorGroup;
  editedGroup: InternalApiListSelectorGroup | undefined;
  setEdit: () => void;
  deleteGroup?: (groupCode: string) => void;
  onSelectGroupCode?: (code: string) => (isToggled: boolean) => void;
  selectedGroupCodes: string[];
  validationResult?: GroupValidationResult;
}

export const SelectorGroup = ({
  name,
  setName,
  navigate,
  isNavigated,
  group,
  editedGroup,
  setEdit,
  deleteGroup,
  onSelectGroupCode,
  selectedGroupCodes,
  validationResult,
}: SelectorGroupProps) => {
  const isEdited = editedGroup?.code === group.code;
  const isDisabled = !!editedGroup && editedGroup.code !== group.code;

  const inputRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    isEdited && inputRef.current?.focus();
  }, [isEdited]);

  return (
    <div
      onClick={() => !isDisabled && navigate()}
      className={clsx('selector-option', {
        'selector-option-navigable': !!navigate,
        'selector-option-navigated': !!isNavigated,
        'selector-option-disabled': isDisabled,
      })}
      aria-label={name}
    >
      <div className={'selector-option-main'}>
        <div className={'selector-option-title'}>
          {isEdited ? (
            <SalvTextArea
              value={name}
              onChange={setName}
              size={Size.EXTRA_SMALL}
              ariaLabel={'Group name'}
              placeholder={'Group name'}
              autoSizing
              minRows={1}
              ref={inputRef}
              isInvalid={validationResult?.nameIsBlank}
            />
          ) : (
            name
          )}
        </div>
        <div className={'selector-option-actions'}>
          {onSelectGroupCode ? (
            <SalvSwitch
              selected={selectedGroupCodes.includes(group.code)}
              onChange={onSelectGroupCode(group.code)}
              ariaLabel={`Group ${group.code}`}
            />
          ) : (
            <>
              <Has allPermissions={[CAN_MODIFY_SCREENING_SELECTOR_GROUP]}>
                <SalvMenuButton
                  onAction={([action]) => {
                    if (action === 'edit') {
                      setEdit();
                    } else if (action === 'delete') {
                      deleteGroup!(group.code);
                    }
                  }}
                  disabled={!!editedGroup}
                  ariaLabel={`Edit Group ${name}`}
                  size={Size.SMALL}
                  placement={'bottom end'}
                >
                  <MenuItem title={'Edit'} value={'edit'} />
                  <MenuItem title={'Delete'} value={'delete'} dangerTextColor />
                </SalvMenuButton>
              </Has>

              <SalvIconButton
                ariaLabel={`Navigate from Group ${name}`}
                icon={faChevronRight}
                onClick={() => navigate()}
                disabled={isDisabled}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
export default SelectorOption;
